import { useState, useEffect, useRef, useCallback } from 'react';

// ⚡ CONFIGURAZIONE ICE MIGLIORATA PER MOBILE
const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' }
  ],
  iceTransportPolicy: 'all',
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require'
};

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const getVideoConstraints = () => {
  if (isMobile()) {
    return {
      width: { ideal: 640, max: 1280 },
      height: { ideal: 480, max: 720 },
      frameRate: { ideal: 24, max: 30 },
      facingMode: 'user'
    };
  }
  return {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 }
  };
};

const useWebRTC = (roomId, userData, socket) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [handRaised, setHandRaised] = useState(false);
  const [raisedHands, setRaisedHands] = useState([]);
  const [whiteboardData, setWhiteboardData] = useState([]);
  const [isRecording, setIsRecording] = useState(false);

  // ✅ FIX: Usa .current per accedere alle ref
  const peerConnections = useRef({});
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const originalVideoTrackRef = useRef(null);
  const hasJoined = useRef(false);

  const initializeMedia = useCallback(async () => {
    if (localStreamRef.current) {
      return localStreamRef.current;
    }

    try {
      console.log(`📱 Initializing media on ${isMobile() ? 'MOBILE' : 'DESKTOP'}`);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: getVideoConstraints(),
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: isMobile() ? 16000 : 48000,
          channelCount: 1
        }
      });

      console.log('✅ Media stream obtained:', {
        videoTrack: stream.getVideoTracks()[0]?.label,
        audioTrack: stream.getAudioTracks()[0]?.label,
        videoSettings: stream.getVideoTracks()[0]?.getSettings()
      });

      localStreamRef.current = stream;
      originalVideoTrackRef.current = stream.getVideoTracks()[0];
      setLocalStream(stream);
      return stream;
    } catch (err) {
      console.error('❌ Errore media:', err);
      setError('Impossibile accedere a camera/microfono. Verifica i permessi.');
      throw err;
    }
  }, []);

  const createPeerConnection = useCallback((participantId, stream) => {
    console.log(`🔗 Creating peer connection for ${participantId}`);
    
    // ✅ FIX: Non ricreare se già esiste
    if (peerConnections.current[participantId]) {
      console.log(`⚠️ Peer connection already exists for ${participantId}, reusing`);
      return peerConnections.current[participantId];
    }
    
    const pc = new RTCPeerConnection(ICE_CONFIG);
    
    if (stream) {
      stream.getTracks().forEach(track => {
        console.log(`➕ Adding track ${track.kind} to ${participantId}`);
        pc.addTrack(track, stream);
      });
    }

    pc.ontrack = (event) => {
      console.log(`🎬 Received track from ${participantId}:`, event.track.kind);
      const [remoteStream] = event.streams;
      setRemoteStreams(prev => ({
        ...prev,
        [participantId]: remoteStream
      }));
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('ice-candidate', {
          roomId,
          targetId: participantId,
          candidate: event.candidate
        });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log(`🔌 Connection state for ${participantId}:`, pc.connectionState);
      if (pc.connectionState === 'connected') {
        setParticipants(prev => prev.map(p =>
          p.id === participantId ? { ...p, connected: true } : p
        ));
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        console.warn(`⚠️ Connection ${pc.connectionState} for ${participantId}`);
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`🧊 ICE state for ${participantId}:`, pc.iceConnectionState);
    };

    peerConnections.current[participantId] = pc;
    return pc;
  }, [roomId, socket]);

  const replaceVideoTrack = useCallback(async (newTrack) => {
    for (const [participantId, pc] of Object.entries(peerConnections.current)) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        try {
          await sender.replaceTrack(newTrack);
          console.log(`✅ Replaced video track for ${participantId}`);
        } catch (err) {
          console.error(`❌ Error replacing track for ${participantId}:`, err);
        }
      }
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (!localStreamRef.current) return;

    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setVideoEnabled(videoTrack.enabled);
      setLocalStream(localStreamRef.current);
      
      if (socket) {
        socket.emit('media-state-changed', {
          roomId,
          videoEnabled: videoTrack.enabled,
          audioEnabled
        });
      }
    }
  }, [roomId, socket, audioEnabled]);

  const toggleAudio = useCallback(() => {
    if (!localStreamRef.current) return;

    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setAudioEnabled(audioTrack.enabled);
      
      if (socket) {
        socket.emit('media-state-changed', {
          roomId,
          videoEnabled,
          audioEnabled: audioTrack.enabled
        });
      }
    }
  }, [roomId, socket, videoEnabled]);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      console.log('⏹️ Stopping screen share');
      setIsScreenSharing(false);
      
      setParticipants(prev => prev.map(p => 
        p.id === 'local' ? { ...p, isSharing: false } : p
      ));
      
      if (socket) socket.emit('screen-share-stopped', { roomId });
      
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      
      if (!localStreamRef.current) return;
      
      try {
        const oldTracks = localStreamRef.current.getVideoTracks();
        oldTracks.forEach(track => {
          localStreamRef.current.removeTrack(track);
          track.stop();
        });
        
        let cameraTrack = originalVideoTrackRef.current;
        
        if (!cameraTrack || cameraTrack.readyState === 'ended') {
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: getVideoConstraints()
          });
          cameraTrack = newStream.getVideoTracks()[0];
          originalVideoTrackRef.current = cameraTrack;
        }
        
        localStreamRef.current.addTrack(cameraTrack);
        cameraTrack.enabled = videoEnabled;
        
        await replaceVideoTrack(cameraTrack);
        setLocalStream(localStreamRef.current);
        
      } catch (err) {
        console.error('❌ Errore ripristino camera:', err);
      }
    } else {
      try {
        console.log('🖥️ Starting screen share');
        
        if (isMobile() && !navigator.mediaDevices.getDisplayMedia) {
          setError('La condivisione schermo non è supportata su questo dispositivo mobile');
          return;
        }
        
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { 
            width: { ideal: 1920, max: 1920 }, 
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 30 }
          },
          audio: false
        });
        
        screenStreamRef.current = screenStream;
        const screenTrack = screenStream.getVideoTracks()[0];
        
        await replaceVideoTrack(screenTrack);
        
        const oldVideoTracks = localStreamRef.current.getVideoTracks();
        oldVideoTracks.forEach(track => localStreamRef.current.removeTrack(track));
        localStreamRef.current.addTrack(screenTrack);
        setLocalStream(localStreamRef.current);
        
        setIsScreenSharing(true);
        setParticipants(prev => prev.map(p => 
          p.id === 'local' ? { ...p, isSharing: true } : p
        ));
        
        if (socket) socket.emit('screen-share-started', { roomId });
        
        screenTrack.onended = () => {
          console.log('📺 Screen share ended by user');
          if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(t => t.stop());
            screenStreamRef.current = null;
          }
          
          (async () => {
            try {
              if (!localStreamRef.current) return;
              
              const oldTracks = localStreamRef.current.getVideoTracks();
              oldTracks.forEach(track => {
                localStreamRef.current.removeTrack(track);
                track.stop();
              });
              
              let cameraTrack = originalVideoTrackRef.current;
              
              if (!cameraTrack || cameraTrack.readyState === 'ended') {
                const newStream = await navigator.mediaDevices.getUserMedia({
                  video: getVideoConstraints()
                });
                cameraTrack = newStream.getVideoTracks()[0];
                originalVideoTrackRef.current = cameraTrack;
              }
              
              localStreamRef.current.addTrack(cameraTrack);
              cameraTrack.enabled = videoEnabled;
              
              await replaceVideoTrack(cameraTrack);
              setLocalStream(localStreamRef.current);
              setIsScreenSharing(false);
              
              setParticipants(prev => prev.map(p => 
                p.id === 'local' ? { ...p, isSharing: false } : p
              ));
              
              if (socket) socket.emit('screen-share-stopped', { roomId });
            } catch (err) {
              console.error('❌ Errore:', err);
              setIsScreenSharing(false);
              setParticipants(prev => prev.map(p => 
                p.id === 'local' ? { ...p, isSharing: false } : p
              ));
              if (socket) socket.emit('screen-share-stopped', { roomId });
            }
          })();
        };
        
      } catch (err) {
        console.error('❌ Errore screen share:', err);
        setIsScreenSharing(false);
        if (err.name !== 'AbortError') {
          setError('Impossibile avviare screen share');
        }
      }
    }
  }, [isScreenSharing, videoEnabled, roomId, socket, replaceVideoTrack]);

  const sendMessage = useCallback((message) => {
    if (socket) {
      const msg = {
        ...message,
        roomId,
        timestamp: new Date().toISOString()
      };
      socket.emit('chat-message', msg);
      
      setMessages(prev => [...prev, {
        ...msg,
        id: Date.now(),
        sender: { id: 'local', name: userData?.displayName || 'Tu' }
      }]);
    }
  }, [socket, roomId, userData]);

  const sendReaction = useCallback((emoji) => {
    if (socket) {
      socket.emit('send-reaction', { roomId, emoji });
    }
  }, [socket, roomId]);

  const toggleHand = useCallback(() => {
    if (socket) {
      socket.emit('toggle-hand', { roomId });
      setHandRaised(prev => !prev);
    }
  }, [socket, roomId]);

  const sendWhiteboardData = useCallback((drawData) => {
    if (socket) {
      socket.emit('whiteboard-draw', { roomId, drawData });
    }
  }, [socket, roomId]);

  const clearWhiteboard = useCallback(() => {
    if (socket) {
      socket.emit('whiteboard-clear', { roomId });
      setWhiteboardData([]);
    }
  }, [socket, roomId]);

  const uploadFile = useCallback((fileData) => {
    if (socket) {
      socket.emit('file-uploaded', { roomId, fileData });
    }
  }, [socket, roomId]);

  const startRecording = useCallback(() => {
    if (socket) {
      socket.emit('start-recording', { roomId });
      setIsRecording(true);
    }
  }, [socket, roomId]);

  const stopRecording = useCallback(() => {
    if (socket) {
      socket.emit('stop-recording', { roomId });
      setIsRecording(false);
    }
  }, [socket, roomId]);

  const leaveRoom = useCallback(() => {
    console.log('👋 Leaving room...');
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`⏹️ Stopped track: ${track.kind}`);
      });
      localStreamRef.current = null;
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    originalVideoTrackRef.current = null;

    Object.entries(peerConnections.current).forEach(([id, pc]) => {
      pc.close();
      console.log(`🔌 Closed connection to ${id}`);
    });
    peerConnections.current = {};

    if (socket) {
      socket.emit('leave-room', roomId);
    }

    setLocalStream(null);
    setRemoteStreams({});
    setParticipants([]);
    setIsConnected(false);
    setIsScreenSharing(false);
    hasJoined.current = false;
  }, [socket, roomId]);

  // ========================================
  // 🔧 MAIN useEffect - JOIN ROOM CON HEARTBEAT E FIX WEBRTC
  // ========================================
  useEffect(() => {
    if (!socket || !roomId || !userData || hasJoined.current) return;

    let mounted = true;
    let roomHeartbeatInterval = null;

    const initialize = async () => {
      try {
        console.log('🚀 Initializing WebRTC...');
        const stream = await initializeMedia();
        
        if (!mounted) return;

        // ✅ Setup heartbeat PRIMA di joinare
        roomHeartbeatInterval = setInterval(() => {
          if (socket && socket.connected) {
            socket.emit('heartbeat');
            console.log('💓 Room heartbeat');
          } else {
            console.warn('⚠️ Socket disconnesso in room, fermo heartbeat');
            clearInterval(roomHeartbeatInterval);
            roomHeartbeatInterval = null;
          }
        }, 10000);

        console.log('✅ Room heartbeat attivato');

        // ==========================================
        // HANDLERS SOCKET EVENTS - FIX PARTICIPANTID
        // ==========================================

        const handleRoomJoined = async (data) => {
          console.log('✅ Room joined successfully', data);
          const existingParticipants = data.participants || [];
          setParticipants(existingParticipants);
          setMessages(data.messages || []);
          setFiles(data.files || []);
          setWhiteboardData(data.whiteboard || []);
          setRaisedHands(data.raisedHands || []);
          setIsConnected(true);
          
          console.log(`📋 Found ${existingParticipants.length} existing participants`);
          for (const participant of existingParticipants) {
            if (participant.id !== 'local') {
              console.log(`🔗 Creating connection for: ${participant.displayName}`);
              const pc = createPeerConnection(participant.id, stream);
              try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                socket.emit('offer', { roomId, targetId: participant.id, offer });
                console.log(`📤 Offer sent to ${participant.id}`);
              } catch (err) {
                console.error(`❌ Error creating offer:`, err);
              }
            }
          }
        };

        const handleRoomError = (data) => {
          console.error('❌ Room error:', data.message);
          setError(data.message);
          setIsConnected(false);
        };

        const handleServerDisconnect = (reason) => {
          console.warn('⚠️ Disconnesso dalla room:', reason);
          
          if (reason === 'io server disconnect') {
            console.error('🚫 Server ha forzato disconnessione');
            setError('Connessione persa. Tentativo di riconnessione...');
          }
          
          setIsConnected(false);
          
          if (roomHeartbeatInterval) {
            console.log('🧹 Cleanup room heartbeat (disconnect)');
            clearInterval(roomHeartbeatInterval);
            roomHeartbeatInterval = null;
          }
        };

        const handleReconnectSuccess = () => {
          console.log('🔄 Riconnesso! Re-join della room...');
          setError(null);
          
          roomHeartbeatInterval = setInterval(() => {
            if (socket && socket.connected) {
              socket.emit('heartbeat');
              console.log('💓 Room heartbeat (dopo reconnect)');
            }
          }, 10000);
          
          console.log('✅ Room heartbeat riavviato');
          
          socket.emit('join-room', {
            roomId,
            userData: {
              ...userData,
              videoEnabled: videoEnabled,
              audioEnabled: audioEnabled
            },
            password: userData.password || null
          });
        };

        const handleUserJoined = async (data) => {
          const user = data.userData || data.user;
          if (!user || !user.displayName) {
            console.error('❌ User joined without proper data:', data);
            return;
          }
          
          console.log('👤 User joined:', user.displayName);
          setParticipants(prev => {
            const exists = prev.some(p => p.id === data.participantId);
            if (exists) return prev;
            return [...prev, { ...user, id: data.participantId }];
          });
        };

        const handleUserLeft = (data) => {
          const userId = data.participantId || data.userId;
          console.log('👋 User left:', userId);
          setParticipants(prev => prev.filter(p => p.id !== userId));
          
          const pc = peerConnections.current[userId];
          if (pc) {
            console.log('🔌 Closing peer connection for', userId);
            pc.close();
            delete peerConnections.current[userId];
            
            setRemoteStreams(prev => {
              const newStreams = { ...prev };
              delete newStreams[userId];
              return newStreams;
            });
          }
        };

        // ✅ FIX CRITICO: Gestione corretta participantId
        const handleOffer = async (data) => {
          const participantId = data.participantId || data.senderId;
          console.log('📥 Received offer from:', participantId);
          
          if (!participantId) {
            console.error('❌ Offer without participant ID:', data);
            return;
          }
          
          try {
            let pc = peerConnections.current[participantId];
            
            if (!pc) {
              console.log('🔗 Creating new peer connection for offer');
              pc = createPeerConnection(participantId, stream);
            }
            
            await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            
            socket.emit('answer', {
              roomId,
              targetId: participantId,
              answer
            });
            
            console.log('📤 Answer sent to', participantId);
          } catch (err) {
            console.error('❌ Error handling offer:', err);
          }
        };

        const handleAnswer = async (data) => {
          const participantId = data.participantId || data.senderId;
          console.log('📥 Received answer from:', participantId);
          
          if (!participantId) {
            console.error('❌ Answer without participant ID:', data);
            return;
          }
          
          try {
            const pc = peerConnections.current[participantId];
            if (pc && pc.signalingState !== 'stable') {
              await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
              console.log('✅ Answer applied');
            }
          } catch (err) {
            console.error('❌ Error handling answer:', err);
          }
        };

        const handleIceCandidate = async (data) => {
          const participantId = data.participantId || data.senderId;
          console.log('🧊 Received ICE candidate from:', participantId);
          
          if (!participantId) {
            console.error('❌ ICE candidate without participant ID:', data);
            return;
          }
          
          try {
            const pc = peerConnections.current[participantId];
            if (pc && data.candidate) {
              await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
              console.log('✅ ICE candidate added');
            }
          } catch (err) {
            console.error('❌ Error adding ICE candidate:', err);
          }
        };

        const handleChatMessage = (data) => {
          console.log('💬 New message:', data.message?.text || data.text);
          setMessages(prev => [...prev, data.message || data]);
        };

        const handleFileShared = (data) => {
          console.log('📎 New file shared:', data.file?.name);
          setFiles(prev => [...prev, data.file]);
        };

        const handleWhiteboardUpdate = (data) => {
          console.log('🎨 Whiteboard updated');
          setWhiteboardData(data.data);
        };

        const handleHandRaised = (data) => {
          const userId = data.participantId || data.userId;
          console.log('✋ Hand raised:', userId);
          setRaisedHands(prev => {
            if (prev.includes(userId)) return prev;
            return [...prev, userId];
          });
        };

        const handleHandLowered = (data) => {
          const userId = data.participantId || data.userId;
          console.log('👎 Hand lowered:', userId);
          setRaisedHands(prev => prev.filter(id => id !== userId));
        };

        const handleParticipantUpdate = (data) => {
          const userId = data.participantId || data.userId;
          console.log('🔄 Participant updated:', userId);
          setParticipants(prev => 
            prev.map(p => 
              p.id === userId 
                ? { ...p, videoEnabled: data.videoEnabled, audioEnabled: data.audioEnabled }
                : p
            )
          );
        };

        // ==========================================
        // REGISTRAZIONE HANDLERS
        // ==========================================

        socket.on('disconnect', handleServerDisconnect);
        socket.on('reconnect', handleReconnectSuccess);
        socket.on('room-joined', handleRoomJoined);
        socket.on('room-error', handleRoomError);
        socket.on('user-joined', handleUserJoined);
        socket.on('user-left', handleUserLeft);
        socket.on('offer', handleOffer);
        socket.on('answer', handleAnswer);
        socket.on('ice-candidate', handleIceCandidate);
        socket.on('chat-message', handleChatMessage);
        socket.on('file-shared', handleFileShared);
        socket.on('whiteboard-update', handleWhiteboardUpdate);
        socket.on('hand-raised', handleHandRaised);
        socket.on('hand-lowered', handleHandLowered);
        socket.on('participant-update', handleParticipantUpdate);

        // JOIN ROOM
        console.log('📤 Sending join-room request...');
        socket.emit('join-room', {
          roomId,
          userData: {
            ...userData,
            videoEnabled: true,
            audioEnabled: true
          },
          password: userData.password || null
        });

        hasJoined.current = true;

        return () => {
          console.log('🧹 Cleaning up room connection...');
          
          if (roomHeartbeatInterval) {
            console.log('🧹 Cleanup room heartbeat interval');
            clearInterval(roomHeartbeatInterval);
            roomHeartbeatInterval = null;
          }
          
          socket.off('disconnect', handleServerDisconnect);
          socket.off('reconnect', handleReconnectSuccess);
          socket.off('room-joined', handleRoomJoined);
          socket.off('room-error', handleRoomError);
          socket.off('user-joined', handleUserJoined);
          socket.off('user-left', handleUserLeft);
          socket.off('offer', handleOffer);
          socket.off('answer', handleAnswer);
          socket.off('ice-candidate', handleIceCandidate);
          socket.off('chat-message', handleChatMessage);
          socket.off('file-shared', handleFileShared);
          socket.off('whiteboard-update', handleWhiteboardUpdate);
          socket.off('hand-raised', handleHandRaised);
          socket.off('hand-lowered', handleHandLowered);
          socket.off('participant-update', handleParticipantUpdate);
        };
      } catch (err) {
        console.error('❌ Initialization error:', err);
        if (mounted) {
          setError(err.message);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      
      if (roomHeartbeatInterval) {
        console.log('🧹 Final cleanup room heartbeat');
        clearInterval(roomHeartbeatInterval);
      }
    };
  }, [socket, roomId, userData, initializeMedia, createPeerConnection, videoEnabled, audioEnabled]);

  useEffect(() => {
    return () => leaveRoom();
  }, [leaveRoom]);

  return {
    localStream,
    remoteStreams,
    participants,
    messages,
    files,
    isConnected,
    videoEnabled,
    audioEnabled,
    isScreenSharing,
    error,
    reactions,
    handRaised,
    raisedHands,
    whiteboardData,
    isRecording,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    sendMessage,
    sendReaction,
    toggleHand,
    sendWhiteboardData,
    clearWhiteboard,
    uploadFile,
    startRecording,
    stopRecording,
    leaveRoom
  };
};

export default useWebRTC;
